import React, { useState, useEffect } from 'react';
import { landlordAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, RotateCcw, Loader2, Shield, CreditCard, FileText, ScrollText } from 'lucide-react';

export default function LandlordLeaseTerms() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [terms, setTerms] = useState({
    house_rules: '',
    payment_terms: '',
    terms_of_service: ''
  });
  const [activeTab, setActiveTab] = useState('house_rules');

  useEffect(() => {
    loadTerms();
  }, []);

  const loadTerms = async () => {
    try {
      const response = await landlordAPI.getLeaseTerms();
      setTerms(response.data);
    } catch (error) {
      toast.error('Failed to load lease terms');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await landlordAPI.updateLeaseTerms(terms);
      toast.success('Lease terms saved successfully');
    } catch (error) {
      toast.error('Failed to save lease terms');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset all lease terms to defaults? This cannot be undone.')) {
      return;
    }
    setResetting(true);
    try {
      const response = await landlordAPI.resetLeaseTerms();
      setTerms(response.data.lease_terms);
      toast.success('Lease terms reset to defaults');
    } catch (error) {
      toast.error('Failed to reset lease terms');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lease-terms-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ScrollText className="w-7 h-7 text-[#C85A42]" />
            Lease Agreement Terms
          </h1>
          <p className="text-muted-foreground mt-1">
            Customize the terms that will be included in all new leases
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={resetting}
            data-testid="reset-terms-btn"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Reset to Defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#C85A42] hover:bg-[#B04A35]"
            data-testid="save-terms-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Lease Terms</CardTitle>
          <CardDescription>
            These terms will automatically be included in all new lease agreements you create.
            Tenants must review and agree to these terms before acknowledging their lease.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="house_rules" className="flex items-center gap-2" data-testid="house-rules-tab">
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">House Rules</span>
              </TabsTrigger>
              <TabsTrigger value="payment_terms" className="flex items-center gap-2" data-testid="payment-terms-tab">
                <CreditCard className="w-4 h-4" />
                <span className="hidden sm:inline">Payment Terms</span>
              </TabsTrigger>
              <TabsTrigger value="terms_of_service" className="flex items-center gap-2" data-testid="tos-tab">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Terms of Service</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="house_rules" className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg mb-4">
                <h3 className="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  House Rules and Regulations
                </h3>
                <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                  Define the rules that tenants must follow while residing in your property.
                </p>
              </div>
              <Textarea
                value={terms.house_rules || ''}
                onChange={(e) => setTerms({ ...terms, house_rules: e.target.value })}
                placeholder="Enter house rules and regulations..."
                className="min-h-[400px] font-mono text-sm"
                data-testid="house-rules-textarea"
              />
            </TabsContent>

            <TabsContent value="payment_terms" className="space-y-4">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg mb-4">
                <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Terms
                </h3>
                <p className="text-emerald-700 dark:text-emerald-300 text-sm mt-1">
                  Specify payment due dates, accepted methods, late fees, and other payment-related policies.
                </p>
              </div>
              <Textarea
                value={terms.payment_terms || ''}
                onChange={(e) => setTerms({ ...terms, payment_terms: e.target.value })}
                placeholder="Enter payment terms..."
                className="min-h-[400px] font-mono text-sm"
                data-testid="payment-terms-textarea"
              />
            </TabsContent>

            <TabsContent value="terms_of_service" className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg mb-4">
                <h3 className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Terms of Service
                </h3>
                <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                  Additional terms and conditions that govern the lease agreement.
                </p>
              </div>
              <Textarea
                value={terms.terms_of_service || ''}
                onChange={(e) => setTerms({ ...terms, terms_of_service: e.target.value })}
                placeholder="Enter terms of service..."
                className="min-h-[400px] font-mono text-sm"
                data-testid="tos-textarea"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card className="border-muted">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Changes to these terms will only apply to new leases created after saving.
            Existing leases will retain their original terms. To update terms for an existing tenant,
            you will need to create a new lease agreement.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
